import React, { useEffect, useRef } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';

const HomeBanner = () => {
    const controls = useAnimation();
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true });

    useEffect(() => {
        if (isInView) {
            controls.start("visible");
        }
    }, [controls, isInView]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.3
            }
        }
    };

    const itemVariants = {
        hidden: { y: 50, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.8,
                ease: [0.16, 0.77, 0.47, 0.97]
            }
        }
    };

    return (
        <section className="relative w-full min-h-screen overflow-hidden bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900" ref={ref}>
            {/* Animated gradient background */}
            <div className="absolute inset-0 z-0 overflow-hidden">
                <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-600 rounded-full mix-blend-soft-light filter blur-xl opacity-30 animate-blob" />
                <div className="absolute top-0 -right-4 w-72 h-72 bg-cyan-500 rounded-full mix-blend-soft-light filter blur-xl opacity-30 animate-blob animation-delay-2000" />
                <div className="absolute -bottom-8 left-20 w-72 h-72 bg-blue-600 rounded-full mix-blend-soft-light filter blur-xl opacity-30 animate-blob animation-delay-4000" />

                {/* Subtle grid overlay */}
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0ibm9uZSIvPjxwYXRoIGQ9Ik0wIDBoNjB2NjBIMHoiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9zdmc+')] opacity-10" />
            </div>

            {/* Content */}
            <div className=" max-w-7xl mx-auto relative z-10 min-h-screen flex items-center px-4 sm:px-6 lg:px-8">
                <motion.div
                    className=" w-full max-w-2xl lg:max-w-7xl mt-16 md:mt-20"
                    variants={containerVariants}
                    initial="hidden"
                    animate={controls}
                >
                    {/* Subheading */}
                    <motion.span
                        className="block text-sm sm:text-base md:text-lg lg:text-xl font-mono text-cyan-400 mb-2 sm:mb-3 md:mb-4 tracking-widest flex items-center gap-2 sm:gap-3"
                        variants={itemVariants}
                    >
                        Empowering Innovation
                        <span className="hidden sm:inline-block h-px bg-cyan-400 flex-1 max-w-[80px] sm:max-w-[120px] md:max-w-[100px] lg:max-w-[100px]" />
                    </motion.span>

                    {/* Main heading with responsive typography */}
                    <motion.div
                        className="mb-2 sm:mb-3 md:mb-4"
                        variants={itemVariants}
                    >
                        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight">
                            Building the Future of Tech
                        </h1>
                    </motion.div>

                    {/* Brand Stories with responsive layout */}
                    <motion.div
                        className="flex flex-col md:flex-row items-start mb-4 sm:mb-6 md:mb-8"
                        variants={itemVariants}
                    >
                        <div className="order-2 md:order-1 w-full md:w-1/2 lg:w-2/5">
                            <p className="text-sm sm:text-base md:text-lg text-gray-200 mb-4 sm:mb-6 md:mb-0 md:mt-5 lg:text-xl">
                                At <strong>UniTechnoStack</strong>, we specialize in transforming ideas into scalable digital products. From <strong>custom software development</strong> and <strong>mobile Game solutions</strong> to <strong>cloud integration</strong> and <strong>AI Solutions</strong>, our team delivers next-gen IT solutions to help businesses innovate, grow, and lead —- across India, the USA, Australia, and Europe.
                            </p>
                        </div>

                        <div className="order-1 md:order-2 w-full md:w-1/2 lg:w-3/5 mb-4 md:mb-0 md:pl-8">
                            <div className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500 text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-none">
                                <div>DIGITAL</div>
                                <div>SOLUTIONS</div>
                            </div>
                        </div>
                    </motion.div>

                 
                </motion.div>
            </div>

            {/* Floating elements for visual interest */}
            <div className="absolute bottom-10 left-10 w-4 h-4 rounded-full bg-cyan-400 opacity-50 animate-float" />
            <div className="absolute top-1/4 right-20 w-6 h-6 rounded-full bg-purple-500 opacity-30 animate-float animation-delay-2000" />
            <div className="absolute top-2/3 left-1/4 w-3 h-3 rounded-full bg-blue-400 opacity-40 animate-float animation-delay-4000" />
        </section>
    );
};

export default HomeBanner;